import { useEffect, useEffectEvent, useState } from "react";
import { Image } from "react-bootstrap";
import { AsyncButton, TypeAheadItem } from "../controls";
import { IRequestStatus, LoadFunctionWithData, loadWrapper, PauseCover } from "../utility";
import { BeFigureContextProvider } from "./BeFigureContextProvider";
import { BeFigureOptions } from "./BeFigureOptions";
import { FigureData } from "./types/FigureData";
import { FigureSettings } from "./types/FigureSettings";

export interface BeFigureProps {
  backEndUrl: string;
  externalFigureRequest: LoadFunctionWithData<FigureData, string>;
  figureInput: FigureData;
  figureSettings: FigureSettings;
  updateFigureInput: (ret: FigureData) => void;
  varNames: TypeAheadItem[];
}

/**
 * Renders the figure from the back end
 *
 * @param param - Destructured parameter object
 * @param param.backEndUrl - Back end URL for API calls
 * @param param.externalFigureRequest - External function to request figure data
 * @param param.figureInput - Figure data to render
 * @param param.figureSettings - Options for figure types
 * @param param.updateFigureInput - Function to partially update the figure data
 * @param param.varNames - List of variable names to choose from
 */
export const BeFigure = ({
  backEndUrl,
  externalFigureRequest,
  figureInput,
  figureSettings,
  updateFigureInput,
  varNames,
}: BeFigureProps): React.ReactElement | null => {
  const [showBeFigure, setShowBeFigure] = useState<boolean>(false);
  const [generatedImage, setGeneratedImage] = useState<string>("");
  const [requestStatus, setRequestStatus] = useState<IRequestStatus<FigureData>>({
    requesting: false,
    error: false,
  });

  /**
   * Load an image based on the current parameters
   */
  const loadFigure = useEffectEvent(async (input: FigureData) => {
    const request = await loadWrapper(
      externalFigureRequest,
      input,
      requestStatus,
      setRequestStatus,
      backEndUrl,
    );

    if (request?.success) {
      setGeneratedImage(request.data);
    }
  });

  /**
   * Reset requesting state on error click
   */
  const handleErrorClick = () => {
    setRequestStatus({ requesting: false, error: false });
  };

  /**
   * Figure load/reload
   */
  useEffect(() => {
    if (!requestStatus.error && !requestStatus.requesting) loadFigure(figureInput);
  }, [figureInput, requestStatus]);

  return (
    <BeFigureContextProvider
      figureInput={figureInput}
      figureSettings={figureSettings}
      varNames={varNames}
      updateFigureInput={updateFigureInput}
    >
      <div className="w-100 mt-3 d-flex justify-content-end">
        <AsyncButton
          id="figure-options-toggle"
          aria-label="Toggle figure options"
          variant="secondary"
          size="sm"
          onClick={() => setShowBeFigure(!showBeFigure)}
          className={"my-2"}
        >
          {showBeFigure ? "Hide" : "Show"} figure options
        </AsyncButton>
      </div>
      <div className="d-flex flex-row">
        <div className="flex-grow-1 d-flex justify-content-center align-items-center">
          <div className="border border-1 border-gray-500 rounded p-2 position-relative d-flex justify-content-center align-items-center">
            {requestStatus.requesting && <PauseCover />}
            {requestStatus.error ? (
              <div
                className="text-center align-items-center"
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  minHeight: "600px",
                }}
              >
                <a
                  className="text-danger"
                  onClick={handleErrorClick}
                  role={"button"}
                >
                  Error loading figure, click to retry
                </a>
              </div>
            ) : generatedImage ? (
              <Image
                id="be-image"
                src={generatedImage}
                alt="Generated figure"
                style={{
                  maxWidth: "100%",
                  maxHeight: "600px",
                  objectFit: "contain",
                }}
              />
            ) : (
              <span>Loading...</span>
            )}
          </div>
        </div>
        <div className="d-flex justify-content-end align-items-start ms-3">
          <BeFigureOptions show={showBeFigure} />
        </div>
      </div>
    </BeFigureContextProvider>
  );
};

BeFigure.displayName = "BeFigure";
